import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./lib/pinchGuard.js"; // sets up document-level gesture/touch handlers
import App from "./App.jsx";
import ErrorBoundary from "./lib/ErrorBoundary.jsx";
import { initSentry } from "./lib/sentry.js";
import { initNative } from "./lib/native.js";
import { getLocale } from "./lib/i18n.js";

// The static loader in index.html is written in Chinese; switch it before
// React mounts for everyone else (inline scripts are blocked by the CSP).
try {
  const t = document.getElementById("initial-loader-text");
  if (t && !(getLocale() || "zh-HK").startsWith("zh")) t.textContent = "Loading PettoBook…";
} catch { /* ignore */ }

// Fire-and-forget — DSN gating happens inside initSentry so this is a no-op
// when not configured. We init before render so even setup-time render
// errors are captured. Errors here must not block app render, but we still
// want to know when Sentry itself fails to load (CSP misconfig, network).
initSentry().catch((e) => {
  // eslint-disable-next-line no-console
  console.error("[PettoBook] initSentry threw unexpectedly:", e);
});

// Capacitor bridge: no-ops on web, sets up status bar / splash / keyboard /
// app lifecycle on a real device. Fire-and-forget — render must not block.
initNative().catch((e) => {
  // eslint-disable-next-line no-console
  console.error("[PettoBook] initNative threw unexpectedly:", e);
});

// Register the service worker on web so the branded customer PWA (旗艦版) is
// installable on Android — Chrome only offers "Install app" when an active SW
// with a fetch handler controls the page. Skipped on Capacitor native (no SW)
// and where unsupported. Push opt-in reuses the same registration.
{
  const isNative = typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();
  if (!isNative && typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[PettoBook] SW register failed:", e);
      });
    });
  }
}

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);

// Hide the static HTML loader once React has actually COMMITTED. render() is
// async, so the next frame can arrive before the first commit — and the
// edge functions (api/*-og.js) put crawler text (#seo-ssr) inside #root, which
// then showed as a bare page of SEO text on every load (e.g. after login)
// until React replaced it. The first commit removes #seo-ssr; wait for that.
const hideLoader = () => {
  const el = document.getElementById("initial-loader");
  if (el) {
    el.style.transition = "opacity 200ms ease";
    el.style.opacity = "0";
    setTimeout(() => el.remove(), 220);
  }
};
const loaderStart = Date.now();
const waitForCommit = () => {
  // 8 s cap: never leave the loader up forever if something else is wrong.
  if (!document.getElementById("seo-ssr") || Date.now() - loaderStart > 8000) hideLoader();
  else requestAnimationFrame(waitForCommit);
};
requestAnimationFrame(waitForCommit);
